import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
    FileText, Plus, ShoppingBag, Image as ImageIcon, Send, 
    Trash2, ExternalLink, Heart, Eye, Sparkles, CheckCircle2 
} from 'lucide-react';
import { getProducts, getUserId } from '../api/client';
import { isRTL } from '../utils/translations';
import Spinner from '../components/Spinner';

export default function SellerPosts() {
    const rtl = isRTL();
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState([]);
    const [posts, setPosts] = useState([]);
    const [showNewModal, setShowNewModal] = useState(false);

    // New Post Form State
    const [caption, setCaption] = useState('');
    const [selectedProductId, setSelectedProductId] = useState('');
    const [imageUrl, setImageUrl] = useState('');

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setLoading(true);
                const userId = getUserId() || localStorage.getItem('userId');
                const prodsRes = await getProducts(0, 50, '', '', '', userId).catch(() => ({ data: [] }));
                const pList = prodsRes?.data?.content || (Array.isArray(prodsRes?.data) ? prodsRes.data : []);
                setProducts(pList);

                // Initial sample posts
                const initial = [
                    {
                        id: 1,
                        caption: rtl 
                            ? 'نعلن عن توفر إصدارات خاصة من الساعات الملكية الموثقة. خصم استثنائي لعملائنا في السعودية والخليج.' 
                            : 'Announcing a special curation of certified royal timepieces. Exclusive pricing for GCC patrons.',
                        productId: pList[0]?.id || 1,
                        productName: pList[0]?.nameAr || pList[0]?.name || 'Royal Chronometer',
                        productPrice: pList[0]?.price || '45,000 SAR',
                        likesCount: 142,
                        viewsCount: 1890,
                        productClicks: 320,
                        date: rtl ? 'منذ يومين' : '2 days ago'
                    }
                ];
                setPosts(initial);
            } catch (err) {
                console.error('Failed to load seller posts:', err);
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, [rtl]);

    const handleCreatePost = (e) => {
        e.preventDefault();
        if (!caption.trim()) return;

        const linkedProd = products.find(p => String(p.id) === String(selectedProductId));

        const newPost = {
            id: Date.now(),
            caption: caption.trim(),
            productId: linkedProd?.id,
            productName: linkedProd ? (linkedProd.nameAr || linkedProd.name) : null,
            productPrice: linkedProd ? `${linkedProd.price} ${linkedProd.currency || 'SAR'}` : null,
            likesCount: 0,
            viewsCount: 1,
            productClicks: 0,
            date: rtl ? 'الآن' : 'Just now'
        };

        setPosts([newPost, ...posts]);
        setCaption('');
        setSelectedProductId('');
        setImageUrl('');
        setShowNewModal(false);
    };

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <h1 style={{ margin: '0 0 6px 0', fontSize: 22, fontWeight: 800, color: 'var(--seller-text, #111827)' }}>
                        {rtl ? 'منشورات المتجر الترويجية (Social Commerce)' : 'Social Commerce Posts'}
                    </h1>
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--seller-muted, #64748b)' }}>
                        {rtl 
                            ? 'نشر تحديثات وإعلانات للمتابعين في خلاصة المتابعة مع ربط مباشر بالمنتجات.' 
                            : 'Broadcast drops and updates to followers with direct canonical product links.'}
                    </p>
                </div>

                <button
                    onClick={() => setShowNewModal(true)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        background: '#FE2C55',
                        color: '#ffffff',
                        padding: '10px 22px',
                        borderRadius: 10,
                        fontWeight: 700,
                        fontSize: 13,
                        border: 'none',
                        cursor: 'pointer',
                        boxShadow: '0 4px 14px rgba(254, 44, 85, 0.35)',
                        transition: 'all 0.2s ease'
                    }}
                >
                    <Plus size={16} />
                    <span>{rtl ? 'إنشاء منشور جديد' : 'Create New Post'}</span>
                </button>
            </div>

            {/* Posts Grid */}
            {loading ? (
                <div style={{ padding: '60px 0', textAlign: 'center' }}><Spinner /></div>
            ) : posts.length === 0 ? (
                <div style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 14, padding: '50px 20px', textAlign: 'center', color: 'var(--seller-muted, #64748b)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <FileText size={44} style={{ opacity: 0.3, marginBottom: 12 }} />
                    <p style={{ margin: 0, fontSize: 14 }}>{rtl ? 'لم تقم بنشر أي منشورات بعد.' : 'No social posts created yet.'}</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {posts.map(post => (
                        <div key={post.id} style={{ background: 'var(--seller-card, #ffffff)', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 14, padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                <span style={{ fontSize: 12, color: 'var(--seller-muted, #64748b)' }}>{post.date}</span>
                                <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--seller-muted, #64748b)' }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Eye size={13} color="#0284c7" /> {post.viewsCount}</span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Heart size={13} color="#FE2C55" /> {post.likesCount}</span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><ShoppingBag size={13} color="#FE2C55" /> {post.productClicks} clicks</span>
                                </div>
                            </div>

                            <p style={{ margin: '0 0 14px 0', fontSize: 14, color: 'var(--seller-text, #111827)', lineHeight: 1.6 }}>
                                {post.caption}
                            </p>

                            {post.productId && (
                                <div style={{ background: '#f8f9fa', border: '1px solid var(--seller-border, #e5e7eb)', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <ShoppingBag size={16} color="#FE2C55" />
                                        <strong style={{ fontSize: 13, color: 'var(--seller-text, #111827)' }}>{post.productName}</strong>
                                        <span style={{ color: '#FE2C55', fontWeight: 700, fontSize: 12 }}>{post.productPrice}</span>
                                    </div>
                                    <Link 
                                        to={`/product/${post.productId}`}
                                        target="_blank"
                                        style={{ color: '#FE2C55', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}
                                    >
                                        <span>{rtl ? 'عرض المنتج' : 'View Product'}</span>
                                        <ExternalLink size={12} />
                                    </Link>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Modal: Create Post */}
            {showNewModal && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                    <div style={{ background: '#ffffff', border: '1px solid #e9ecef', borderRadius: 16, width: '100%', maxWidth: 540, padding: '24px', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: 17, fontWeight: 800, color: '#111827' }}>
                            {rtl ? 'إنشاء منشور تجاري جديد' : 'New Social Commerce Post'}
                        </h3>

                        <form onSubmit={handleCreatePost} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                                    {rtl ? 'نص المنشور / الإعلان' : 'Caption / Announcement'}
                                </label>
                                <textarea
                                    rows={4}
                                    value={caption}
                                    onChange={e => setCaption(e.target.value)}
                                    placeholder={rtl ? 'شارك المتابعين بآخر المنتجات، العروض، أو أوقات البث الحي...' : 'Write an announcement or product drop...'}
                                    style={{ width: '100%', padding: '10px 14px', background: '#f8f9fa', border: '1px solid #d1d5db', borderRadius: 8, color: '#111827', fontSize: 13, boxSizing: 'border-box' }}
                                    required
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                                    {rtl ? 'ربط منتج من كتالوجك (اختياري)' : 'Link Product from Catalog (Optional)'}
                                </label>
                                <select
                                    value={selectedProductId}
                                    onChange={e => setSelectedProductId(e.target.value)}
                                    style={{ width: '100%', padding: '10px 14px', background: '#f8f9fa', border: '1px solid #d1d5db', borderRadius: 8, color: '#111827', fontSize: 13, boxSizing: 'border-box' }}
                                >
                                    <option value="">{rtl ? '-- بدون ربط منتج --' : '-- No product link --'}</option>
                                    {products.map(p => (
                                        <option key={p.id} value={p.id}>
                                            {p.nameAr || p.name} ({p.price} {p.currency || 'SAR'})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
                                <button
                                    type="button"
                                    onClick={() => setShowNewModal(false)}
                                    style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#6b7280', padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                                >
                                    {rtl ? 'إلغاء' : 'Cancel'}
                                </button>
                                <button
                                    type="submit"
                                    style={{ background: 'linear-gradient(135deg, #FE2C55 0%, #E01E45 100%)', border: 'none', color: '#fff', padding: '9px 22px', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13, boxShadow: '0 2px 8px rgba(254,44,85,0.3)' }}
                                >
                                    {rtl ? 'نشر الآن' : 'Publish Post'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
